<template>
  <div class="table-wrapper">
    <el-row
      class="table-wrapper__top-actions"
      :gutter="20"
      type="flex"
    >
      <el-col :span="22">
        <div
          class="massive-actions"
          v-if="enableSelection && massiveActionsOptions.length"
        >
          <label class="massive-actions__label">
            {{ $t('table.massiveActions.title') }}
          </label>
          <div class="massive-actions__select">
            <el-select
              v-model="massiveActionsValue"
              :disabled="!selectedRows.length"
              :placeholder="$t('global.select')"
              size="mini"
            >
              <el-option
                v-for="item in massiveActionsOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
                :disabled="item.disabled"
              />
            </el-select>
            <el-button
              :disabled="
                massiveActionsValue == null ||
                massiveActionsValue === '' ||
                !selectedRows.length
              "
              type="primary"
              size="mini"
              @click="handleMassiveAction"
            >
              {{ $t('table.massiveActions.submit') }}
            </el-button>
          </div>
        </div>
      </el-col>
    </el-row>

    <div class="table-content">
      <el-table
        ref="multipleTable"
        :row-key="rowKey"
        :default-sort="sorting"
        :data="tableRows"
        :height="height"
        :cell-class-name="tableCellClassName"
        :row-class-name="customRowClass"
        :lazy="true"
        :load="handleLoadChildren"
        :tree-props="{ children: 'children', hasChildren: 'hasChildren' }"
        border
        @sort-change="sortChange"
      >
        <el-table-column
          label=""
          align="center"
          width="55"
          v-if="canUseBulkApply"
          :key="refreshHeaderCheckbox"
        >
          <template slot-scope="scope">
            <el-checkbox
              :value="
                checkBoxChecked(
                  scope.row,
                  scope.row.hasChildren ? 'parent' : 'child'
                )
              "
              :indeterminate="
                checkBoxIndeterminate(
                  scope.row,
                  scope.row.hasChildren ? 'parent' : 'child'
                )
              "
              @change="
                (value) =>
                  handleSelectionChange(
                    scope.row,
                    value,
                    scope.row.hasChildren ? 'parent' : 'child'
                  )
              "
            />
          </template>
          <template slot="header">
            <el-checkbox
              :value="checkBoxChecked(null, 'header')"
              :indeterminate="checkBoxIndeterminate(null, 'header')"
              @change="(value) => handleSelectionChange(null, value, 'header')"
            />
          </template>
        </el-table-column>

        <el-table-column
          v-for="(column, index) in filteredColumns"
          :key="index"
          :prop="index"
          :label="$t(`filters.${column.label}`)"
          :sortable="column.sortable"
          :align="column.align"
          :min-width="column.minWidth ? column.minWidth : ''"
          :fixed="column.fixed ? 'right' : false"
        >
          <template slot-scope="scope">
            <span v-if="column.type === 'text'">{{
              propertyValue(scope.row, column.key)
            }}</span>
            <span v-if="column.type === 'translatedText'">
              {{ $t(`filters.${propertyValue(scope.row, column.key)}`) }}
            </span>
            <span v-if="column.type === 'boolean'">
              <span
                :style="{ cursor: column.emit ? 'pointer' : 'auto' }"
                @click="
                  () => {
                    if (column.emit) $emit(column.emit, scope.row);
                  }
                "
              >
                <i
                  v-if="scope.row[index]"
                  class="state-icon fas fa-check-circle"
                />
                <i
                  v-else
                  class="state-icon fas fa-do-not-enter"
                />
              </span>
            </span>
            <span v-if="column.type === 'date'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key) | date('DD MMMM YYYY')
                }}
              </span>
            </span>
            <span v-if="column.type === 'datetime'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  propertyValue(scope.row, column.key)
                    | datetime('DD MMMM YYYY HH:mm')
                }}
              </span>
            </span>
            <span v-if="column.type === 'enum'">
              <span v-if="propertyValue(scope.row, column.key)">
                {{
                  $t(
                    `enums.${column.enum}.${propertyValue(
                      scope.row,
                      column.key
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'encoded'">
              <span v-if="propertyValue(scope.row, column.key) !== ''">
                {{
                  $t(
                    `enums.${column.enum}.${column.decode(
                      propertyValue(scope.row, column.key)
                    )}`
                  )
                }}
              </span>
            </span>
            <span v-if="column.type === 'function'">
              {{ column.get(propertyValue(scope.row, column.key), scope.row) }}
            </span>
            <span v-if="column.type === 'function-i18n'">
              {{
                $t(column.get(propertyValue(scope.row, column.key), scope.row))
              }}
            </span>
            <span
              v-if="column.type === 'slot-collapse-list'"
              class="slot-collapse-list"
            >
              <el-collapse accordion>
                <el-collapse-item
                  :title="$t(column.collapseTitle)"
                  name="slot-collapse-item"
                >
                  <a
                    v-for="(el, collapseIndex) in scope.row[column.collapseKey]"
                    :key="`${column.collapseKey}-${collapseIndex}`"
                    href="#"
                    @click.prevent="$emit('collapseItemClick', scope.row, el)"
                  >
                    <i
                      :class="`state-icon fas fa-${column.collapseItemIcon}`"
                    />
                    {{ el[column.collapseValue] }}
                  </a>
                </el-collapse-item>
              </el-collapse>
            </span>
            <TableActions
              v-else-if="column.type === 'actions'"
              :has-permissions="hasPermissions"
              :configuration="column"
              :row="scope.row"
              @actionClick="$emit($event, scope.row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>

    <ViewActionButtons :buttons="actionButtons" />
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';
  import lodashGet from 'lodash.get';
  import TableActions from '@/components/TableActions.vue';
  import ViewActionButtons from '@/components/ViewActionButtons.vue';

  import acl from '@/services/acl';

  export default {
    name: 'ReceivedSamplesTable',
    components: {
      TableActions,
      ViewActionButtons,
    },
    props: {
      tableColumns: {
        type: Object,
        default: () => {},
      },
      tableKey: {
        type: String,
        required: true,
      },
      enableSelection: {
        type: Boolean,
        default: false,
      },
      bulkDisable: {
        type: Boolean,
        default: false,
      },
      customBulkAction: {
        type: Boolean,
        deafult: false,
      },
      customBulkActionOptions: {
        type: Object,
        default: () => {},
      },
      hasPermissions: {
        type: Boolean,
        default: true,
      },
      columnCondition: {
        type: String,
        default: '',
      },
      massiveActionsOptions: {
        type: Array,
        default: () => [],
      },
      height: {
        type: String,
        default: undefined,
      },
      rowKey: {
        type: String,
        default: undefined,
      },
      treeProps: {
        type: Object,
        default: undefined,
      },
      showPagination: { type: Boolean, default: true },
      configurableColumns: { type: Boolean, deafult: false },
      visibleColumns: { type: Array, default: () => [] },
      customRowClass: { type: Function, default: () => '' },
      loadChildren: { type: Function, required: true },
    },
    data() {
      return {
        multipleSelection: [],
        massiveActionsValue: '',
        showConfigOptions: false,
        selectedRows: [],
        refreshHeaderCheckbox: 0,
        actionButtons: [
          {
            text: this.$t('menuItems.groupingInfo'),
            type: 'default',
            action: () => {
              this.viewGroupingsInfo();
            },
          },
        ],
        selectableRowsMap: {},
        loadedChildren: {},
      };
    },
    computed: {
      ...mapGetters(['tablesStatus', 'enabledColumns']),
      sorting() {
        return this.tablesStatus[this.tableKey].sorting;
      },
      currentPage() {
        return this.tablesStatus[this.tableKey].page;
      },
      perPage() {
        return this.tablesStatus[this.tableKey].perPage;
      },
      count() {
        return this.tablesStatus[this.tableKey].count;
      },
      tableRows() {
        return this.tablesStatus[this.tableKey].rows;
      },
      columns() {
        return Object.keys(this.tableColumns).reduce((columns, key) => {
          if (
            acl.userCanUse(this.tableColumns[key].permissions) &&
            this.checkColumnCondition(this.tableColumns[key])
          ) {
            return { ...columns, [key]: this.tableColumns[key] };
          }
          return columns;
        }, {});
      },
      filteredColumns() {
        const enabledColumns = this.enabledColumns(this.tableKey);
        if (enabledColumns.length) {
          return Object.keys(this.columns).reduce((columns, key) => {
            if (
              enabledColumns.includes(this.columns[key].label) ||
              this.columns[key].type === 'actions'
            ) {
              return { ...columns, [key]: this.columns[key] };
            }
            return columns;
          }, {});
        }
        return this.columns;
      },
      totalSelectableCount() {
        return Object.values(this.selectableRowsMap).reduce(
          (acc, arr) => acc + arr.length,
          0
        );
      },
      canUseBulkApply() {
        return acl.userCanUse(['receivedSamplesManage']);
      },
      objectRows() {
        const selectedSet = new Set(this.selectedRows);
        return Object.values(this.loadedChildren)
          .flat()
          .filter((row) => selectedSet.has(row.id));
      },
    },
    mounted() {
      this.$emit('reload');
    },
    watch: {
      tableRows: {
        immediate: true,
        handler(newRows, oldRows) {
          if (newRows && newRows.length > 0) {
            // Check if this was just a group expansion (childrenLoaded changed from false to true)
            // When sorting/filtering, API returns fresh groups without childrenLoaded flag,
            // so this will only be true during actual group expansion
            const isGroupExpansion = newRows.some((newGroup) => {
              const oldGroup = (oldRows || []).find(
                (o) => o.groupingId === newGroup.groupingId
              );
              return (
                oldGroup && !oldGroup.childrenLoaded && newGroup.childrenLoaded
              );
            });

            // Clear selections UNLESS it's just a group expansion
            // This clears selections on filter changes, sorting, and refresh
            if (!isGroupExpansion) {
              this.selectedRows = [];
            }

            // Build fresh maps to avoid stale entries
            const newSelectableRowsMap = {};
            const newLoadedChildren = {};

            newRows.forEach((group) => {
              if (
                group.childrenLoaded &&
                group.children &&
                group.children.length > 0
              ) {
                newSelectableRowsMap[group.groupingId] = group.children.map(
                  (child) => child.id
                );
                newLoadedChildren[group.groupingId] = group.children;
              }
            });

            this.selectableRowsMap = newSelectableRowsMap;
            this.loadedChildren = newLoadedChildren;

            // Clean selections to only include valid IDs from current groups
            const allValidIds = new Set(
              Object.values(newSelectableRowsMap).flat()
            );
            this.selectedRows = this.selectedRows.filter((id) =>
              allValidIds.has(id)
            );
          } else {
            this.selectableRowsMap = {};
            this.loadedChildren = {};
            this.selectedRows = [];
          }

          // Refresh header checkbox to reflect any selection changes
          this.refreshHeaderCheckbox += 1;
        },
      },
    },
    methods: {
      ...mapActions([
        'updateTableSorting',
        'updateTablePerPage',
        'updateTableCurrentPage',
        'updateColumnSort',
      ]),
      checkColumnCondition(column) {
        return (
          !column.columnCondition ||
          (column.columnCondition &&
            column.columnCondition === this.columnCondition)
        );
      },
      tableCellClassName({ row, column }) {
        if (
          this.columns[column.property] &&
          this.columns[column.property].customClass
        ) {
          const modifier =
            this.columns[column.property].getClassModifier &&
            `table-cell-${this.columns[column.property].key}--${this.columns[
              column.property
            ].getClassModifier(row[column.property])}`;
          return `table-cell-${this.columns[column.property].key} ${
            modifier || ''
          }`;
        }
        return '';
      },
      async handleLoadChildren(row, treeNode, resolve) {
        // Carica i children tramite la funzione passata dal parent
        await this.loadChildren(row, treeNode, (children) => {
          // Aggiorna le mappe locali
          this.loadedChildren[row.groupingId] = children;
          this.selectableRowsMap[row.groupingId] = children.map((c) => c.id);
          resolve(children);
        });
      },
      syncLazyChildren(rows) {
        // el-table renders lazy children from store.states.lazyTreeNodeMap
        // (keyed by row-key) and never invalidates it when :data is replaced,
        // so refreshed rows must be pushed into the map or expanded groups
        // keep showing the previously cached children
        const table = this.$refs.multipleTable;
        if (!table || !this.rowKey) return;
        const { lazyTreeNodeMap } = table.store.states;
        const freshChildren = {};
        rows.forEach((row) => {
          if (row.childrenLoaded) {
            freshChildren[row[this.rowKey]] = row.children || [];
          }
        });
        Object.keys(lazyTreeNodeMap).forEach((key) => {
          if (freshChildren[key]) {
            this.$set(lazyTreeNodeMap, key, freshChildren[key]);
          } else {
            // group no longer in the current data set
            this.$delete(lazyTreeNodeMap, key);
          }
        });
      },
      async handleSelectionChange(row, value, type) {
        const selectedSet = new Set(this.selectedRows);

        switch (type) {
          case 'header':
            if (value) {
              // Carica tutti i children se necessario prima di selezionare tutto
              const loadPromises = this.tableRows.map(async (group) => {
                if (!group.childrenLoaded) {
                  return new Promise((resolve) => {
                    this.handleLoadChildren(group, null, resolve);
                  });
                }
                return Promise.resolve(group.children);
              });

              await Promise.all(loadPromises);

              // Seleziona tutti i children caricati
              this.selectedRows = Object.values(this.loadedChildren)
                .flat()
                .map((c) => c.id);
            } else {
              this.selectedRows = [];
            }
            break;

          case 'parent': {
            if (!row.childrenLoaded) {
              // Mostra un loading indicator (opzionale)
              const loading = this.$loading({
                // eslint-disable-next-line no-restricted-globals
                target: event?.target?.closest('tr'),
                lock: true,
                text: 'Loading...',
                spinner: 'el-icon-loading',
                background: 'rgba(255, 255, 255, 0.7)',
              });

              try {
                await new Promise((resolve) => {
                  this.handleLoadChildren(row, null, resolve);
                });
              } finally {
                if (loading) loading.close();
              }
            }

            const childIds = this.selectableRowsMap[row.groupingId] || [];

            if (value) {
              childIds.forEach((id) => selectedSet.add(id));
            } else {
              childIds.forEach((id) => selectedSet.delete(id));
            }

            this.selectedRows = Array.from(selectedSet);
            break;
          }

          case 'child':
          default:
            if (value) {
              selectedSet.add(row.id);
            } else {
              selectedSet.delete(row.id);
            }

            this.selectedRows = Array.from(selectedSet);
            break;
        }

        this.refreshHeaderCheckbox += 1;
      },
      checkBoxChecked(row, type) {
        const selectedSet = new Set(this.selectedRows);

        switch (type) {
          case 'header': {
            // Se non ci sono children caricati, la checkbox header è deselezionata
            if (this.totalSelectableCount === 0) {
              return false;
            }
            return (
              this.totalSelectableCount > 0 &&
              selectedSet.size === this.totalSelectableCount
            );
          }

          case 'parent': {
            const childIds = this.selectableRowsMap[row.groupingId] || [];
            if (childIds.length === 0 && !row.childrenLoaded) {
              // Il gruppo non è selezionato se non ha children caricati
              return false;
            }
            return (
              childIds.length > 0 && childIds.every((id) => selectedSet.has(id))
            );
          }

          case 'child':
          default:
            return selectedSet.has(row.id);
        }
      },
      checkBoxIndeterminate(row, type) {
        const selectedSet = new Set(this.selectedRows);

        switch (type) {
          case 'header': {
            // Se non ci sono children caricati, non è indeterminato
            if (this.totalSelectableCount === 0) {
              return false;
            }
            return (
              selectedSet.size > 0 &&
              selectedSet.size < this.totalSelectableCount
            );
          }

          case 'parent': {
            const childIds = this.selectableRowsMap[row.groupingId] || [];
            if (childIds.length === 0 && !row.childrenLoaded) {
              return false;
            }

            const hasAny = childIds.some((id) => selectedSet.has(id));
            const hasAll = childIds.every((id) => selectedSet.has(id));
            return hasAny && !hasAll;
          }

          case 'child':
          default:
            return false;
        }
      },
      async handleMassiveAction() {
        // Prima di eseguire l'azione, assicurati che tutti i children siano caricati
        const groupsToLoad = [];

        this.selectedRows.forEach((selectedId) => {
          // Trova a quale gruppo appartiene questo ID usando array iteration
          Object.entries(this.selectableRowsMap).some(
            ([groupingId, childIds]) => {
              if (childIds.includes(selectedId)) {
                // Verifica se il gruppo ha tutti i children caricati
                const group = this.tableRows.find(
                  (r) => r.groupingId === Number(groupingId)
                );
                if (group && !group.childrenLoaded) {
                  groupsToLoad.push(group);
                }
                return true;
              }
              return false;
            }
          );
        });

        // Carica i children mancanti
        if (groupsToLoad.length > 0) {
          await Promise.all(
            groupsToLoad.map(
              (group) =>
                new Promise((resolve) => {
                  this.handleLoadChildren(group, null, resolve);
                })
            )
          );
        }

        // Ora emetti l'azione con gli oggetti completi
        this.$emit('massiveAction', this.massiveActionsValue, this.objectRows);
      },
      sortChange(sorting) {
        const { column, ...sortData } = sorting;
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              sorting: JSON.stringify({
                ...sortData,
                prop: this.columns[sortData.prop].key,
              }),
            },
          })
          .catch(() => {});
      },
      handleSizeChange(perPage) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              perPage,
              page: 1,
            },
          })
          .catch(() => {});
      },
      handleCurrentChange(page) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              tableKey: this.tableKey,
              page,
            },
          })
          .catch(() => {});
      },
      propertyValue(object, path) {
        return lodashGet(object, path, '');
      },
      viewGroupingsInfo() {
        this.$router.push({ name: 'groupingInfo' });
      },
    },
  };
</script>

<style lang="scss">
  .table-wrapper {
    .el-table {
      &__row--level-1 {
        .cell .el-checkbox {
          display: inline-block;
        }
      }
    }
  }
</style>
